import { KW_FALLBACK } from './mapdefs';
import type { Project, Collect, Item, PersonCard, CoCard, AnyCard } from './types';

/** 직전 3개년 표기 — 예: 2023~2025 (매년 바뀌므로 고정하지 않는다) */
export function finYears(): string { const y = new Date().getFullYear(); return (y - 3) + '~' + (y - 1); }

export function rid(): string {
  return Math.random().toString(36).slice(2, 7);
}
export function tok(): string {
  return Math.random().toString(36).slice(2, 8);
}

export function itm(t: string, ty?: string, req?: number, sens?: number, map?: string): Item {
  return { id: rid(), title: t, type: ty || 'text', required: !!req, sensitive: !!sens, map: map || '', desc: '', links: [], files: [], open: false };
}

export const gpItems = (): Item[] => [
  itm('엔젤투자교육 사이트 ID/PW', 'text', 1, 1, 'eduIdPw'),
  itm('조합 소재지', 'text', 1, 0, 'orgAddr'),
  itm('주민등록번호', 'text', 1, 1),
  itm('거주지 주소', 'text', 1),
  itm('최종학력', 'text'),
  itm('주요경력', 'text_long'),
  itm('신분증 사본', 'file', 1, 1),
  itm('주민등록등본', 'file', 1, 1),
  itm('엔젤투자교육 수료증', 'file', 1),
];
export const lpItems = (): Item[] => [itm('주민등록등본', 'file', 1, 1), itm('휴대폰번호', 'text', 1)];
export const coItems = (): Item[] => [
  itm('벤처기업 확인시스템 ID·PW', 'text', 1, 1),
  itm('사업자등록증', 'file', 1),
  itm('정관', 'file', 1),
  itm('주주명부', 'file', 1),
  itm('법인 등기부등본', 'file', 1),
  itm(finYears() + ' 표준재무제표', 'file', 1),
  itm('최근 부가세 신고서', 'file', 1),
  itm('중소기업확인서', 'file', 1),
];

export function newLP(name: string, tel: string): PersonCard {
  return { id: rid(), name: name || '', bizno: '', url: '', recips: [{ name: name || '', role: 'LP 본인', tel: tel || '', email: '' }], items: lpItems(), sent: false, results: null };
}

export function buildCollect(p: Project): Collect {
  const c: Collect = {
    gp: { name: p.gp || 'GP', bizno: '', url: '', recips: [{ name: p.gp || 'GP', role: 'GP 본인', tel: p.gpTel || '', email: '' }], items: gpItems(), sent: false, results: null },
    lps: [],
    cos: [],
  };
  const cs = (p.cos || []).filter((x) => !!(x.name || x.bizno || x.ceo));
  cs.forEach((co, i) => {
    let rep: string;
    if (i === 0 && p.gp) {
      rep = 'gp';
    } else {
      const lp = newLP(co.ceo || '', co.mgrTel || '');
      c.lps.push(lp);
      rep = 'lp:' + lp.id;
    }
    c.cos.push({ id: rid(), name: co.name, bizno: co.bizno || '', rep, items: coItems() });
  });
  if (c.lps.length === 0) c.lps.push(newLP('', ''));
  c.cos.forEach((co) => { if (co.rep === 'gp' && !c.gp.bizno) c.gp.bizno = co.bizno; });
  return c;
}

export function cardType(ref: string): 'gp' | 'lp' | 'co' {
  return ref === 'gp' ? 'gp' : ref.startsWith('lp:') ? 'lp' : 'co';
}
export function getCard(c: Collect, ref: string): AnyCard {
  if (ref === 'gp') return c.gp;
  if (ref.startsWith('lp:')) return c.lps.find((l) => l.id === ref.slice(3)) as PersonCard;
  return c.cos.find((co) => co.id === ref.slice(3)) as CoCard;
}
export function getPerson(c: Collect, ref: string): PersonCard {
  return getCard(c, ref) as PersonCard;
}

export function collectItemsFor(c: Collect, ref: string): { it: Item; grp: string | null }[] {
  const card = getCard(c, ref);
  const arr: { it: Item; grp: string | null }[] = card.items.map((it) => ({ it, grp: null }));
  if (ref === 'gp' || ref.startsWith('lp:')) {
    c.cos.filter((co) => co.rep === ref).forEach((co) => co.items.forEach((it) => arr.push({ it, grp: co.name })));
  }
  return arr;
}

/** 포털(자료수집 프로젝트)로 보낼 항목 — 조합 카드 · LP 후보 「정보 받기 링크」가 같은 모양으로 (2026-10-12) */
export interface PortalItem {
  clientId: string; label: string; type: string; required: boolean; sensitive: boolean; section: string | null; description: string;
  guides: { type: string; label: string; url?: string; fileName?: string; filePath?: string; s3Key?: string }[];
}
/** 개인 자료 묶음 이름 — 포털 왼쪽 묶음 · 머리에 「기본 자료」 대신 (2026-10-18 지도사 「기본 자료라고 하지 말고 GP(조합장) 개인자료」) */
export function personalSection(ref: string): string | null {
  return ref === 'gp' ? 'GP(조합장) 개인자료' : ref.startsWith('lp:') ? 'LP(출자자) 개인자료' : null;
}
export function portalItems(c: Collect, ref: string): PortalItem[] {
  const own = personalSection(ref);
  return collectItemsFor(c, ref).map(({ it, grp }) => ({
    clientId: it.id,
    label: it.title,
    type: it.type,
    required: !!it.required,
    sensitive: !!it.sensitive,
    section: grp || own,
    description: it.desc || '',
    guides: [
      ...it.links.filter((l) => l.url || l.name).map((l) => ({ type: 'link', label: l.name || '', url: l.url || '' })),
      ...it.files.filter((f) => f.name).map((f) => ({ type: 'file', label: f.name || '', fileName: f.name || '', filePath: f.filePath || f.s3Key || '', s3Key: f.s3Key || f.filePath || '' })),   // 파일 경로도 같이 — 다시 동기화해도 첨부 가이드가 살아 있게(2026-11-01)
    ],
  }));
}

export function rowStatus(card: PersonCard): { cls: string; label: string } {
  if (!card.sent) return { cls: 'p-gray', label: '대기' };
  if (card.results && card.results.length && card.results.every((r) => r.status === 'approved')) return { cls: 'p-green', label: '완료' };
  return { cls: 'p-blue', label: '전송완료' };
}
export function cardDisplayName(c: Collect, ref: string): string {
  const card = getCard(c, ref);
  const t = cardType(ref);
  return t === 'gp' ? (card.name || 'GP') : t === 'lp' ? (card.name || 'LP (이름 미입력)') : (card.name || '(법인명 미입력)');
}
export function collectRows(c: Collect): { ref: string; type: 'gp' | 'lp'; card: PersonCard }[] {
  const rows: { ref: string; type: 'gp' | 'lp'; card: PersonCard }[] = [{ ref: 'gp', type: 'gp', card: c.gp }];
  c.lps.forEach((l) => rows.push({ ref: 'lp:' + l.id, type: 'lp', card: l }));
  return rows;
}

export function setItemMap(c: Collect, ref: string, j: number, v: string): void {
  const card = getCard(c, ref);
  if (v) card.items.forEach((it, k) => { if (k !== j && it.map === v) it.map = ''; });
  card.items[j].map = v;
}

export function setLpName(c: Collect, ref: string, v: string): void {
  const card = getCard(c, ref) as PersonCard;
  card.name = v;
  if (card.recips && card.recips[0] && (card.recips[0].role || '').indexOf('본인') >= 0) card.recips[0].name = v;
}

export function repOptions(c: Collect): { value: string; label: string }[] {
  const out: { value: string; label: string }[] = [{ value: 'gp', label: 'GP' + (c.gp.name ? ' (' + c.gp.name + ')' : '') }];
  c.lps.forEach((l, i) => out.push({ value: 'lp:' + l.id, label: 'LP' + (i + 1) + (l.name ? ' (' + l.name + ')' : '') }));
  return out;
}

export function setRep(c: Collect, coId: string, rep: string): void {
  const co = c.cos.find((x) => x.id === coId);
  if (!co) return;
  co.rep = rep;
  if (rep === 'gp' && !c.gp.bizno) c.gp.bizno = co.bizno;
  if (rep.startsWith('lp:')) { const l = c.lps.find((x) => 'lp:' + x.id === rep); if (l && !l.bizno) l.bizno = co.bizno; }
}

export function gvOf(card: PersonCard, t: string): string {
  const it = card.items.find((x) => x.title.includes(t));
  if (it && card.sent && card.results) {
    const r = card.results.find((rr) => rr.title === it.title);
    if (r && r.status !== 'none') return r.value;
  }
  return '';
}

export function itemMaps(it: Item): string[] {
  return it.maps && it.maps.length ? it.maps : (it.map ? [it.map] : []);
}

function rvByTitle(card: PersonCard, title: string): string {
  if (card.sent && card.results) {
    const r = card.results.find((x) => x.title === title && x.status !== 'none');
    if (r) return r.value;
  }
  return '';
}

/** 명시 단계매핑 우선(값 있는 항목까지 탐색) → 제목 키워드 폴백 → 결과행 직접 탐색(항목명 개명 등 제목 불일치 대비) */
export function mval(card: PersonCard, key: string): string {
  const mapped = card.items.filter((x) => itemMaps(x).includes(key));
  for (let i = 0; i < mapped.length; i++) {
    const v = rvByTitle(card, mapped[i].title);
    if (v) return v;
  }
  const kw = KW_FALLBACK[key];
  if (!kw) return '';
  const v2 = gvOf(card, kw);
  if (v2) return v2;
  if (card.sent && card.results) {
    const wantFile = mapped[0] ? mapped[0].type === 'file' : null;
    const r = card.results.find((rr) =>
      rr.title.includes(kw) && rr.status !== 'none' && !!rr.value &&
      (wantFile === null || (rr.type === 'file') === wantFile));
    if (r) return r.value;
  }
  return '';
}

/** 연동 파일명 → 제출 uploadId (미리보기·ZIP 첨부 공용). 전체 카드 결과에서 value 일치 + uploadId 보유 행 탐색 */
export function coUploadId(c: Collect, name: string): string {
  if (!name) return '';
  const cards = [c.gp, ...(c.lps || [])];
  for (let i = 0; i < cards.length; i++) {
    const cd = cards[i];
    if (!cd || !cd.collectProjectId || !cd.results) continue;
    const r = cd.results.find((x) => x.value === name && x.uploadId);
    if (r && r.uploadId) return r.uploadId;
  }
  return '';
}

export function coVal(c: Collect, co: CoCard, key: string): string {
  if (!co.rep) return '';
  const person = getPerson(c, co.rep);
  if (!person || !person.items) return '';
  const find = (pred: (t: string) => boolean): string => {
    if (person.sent && person.results) {
      const r = person.results.find((rr) => rr.grp === co.name && pred(rr.title) && rr.status !== 'none');
      if (r) return r.value;
    }
    return '';
  };
  const it = co.items.find((x) => itemMaps(x).includes(key));
  if (it) return find((t) => t === it.title);
  const kw = KW_FALLBACK[key];
  return kw ? find((t) => t.indexOf(kw) >= 0) : '';
}


/** 포털 항목 → 편집용 Item (LP 후보 「항목 편집」이 지금 포털을 불러올 때 · 2026-11-01) */
export function itemFromPortal(x: { clientId: string; label: string; type: string; required: boolean; sensitive: boolean; description: string; guides: { type: string; label: string; url?: string; fileName?: string; filePath?: string; s3Key?: string }[] }): Item {
  return {
    id: x.clientId || rid(), title: x.label || '', type: x.type || 'file', required: x.required !== false, sensitive: !!x.sensitive, map: '', desc: x.description || '',
    links: (x.guides || []).filter((g) => g.type !== 'file').map((g) => ({ name: g.label || g.url || '', url: g.url || '' })),
    files: (x.guides || []).filter((g) => g.type === 'file').map((g) => ({ name: g.fileName || g.label || '', filePath: g.filePath || g.s3Key || '', s3Key: g.s3Key || g.filePath || '' })),
    open: false,
  };
}
